import React, {FC} from "react"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Flex from "@irati/ui/src/Flex"
import {Label, H3} from "@irati/ui/src/Typography"
import {PaymentCurrencyType, Wallet} from "@irati/types"
import {useTheme} from "@irati/ui/src/global/useTheme"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"

import {Card} from "@irati/ui/src/Layout"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Badge from "@irati/ui/src/Badge"
import Loading from "@irati/ui/src/Loading"
import {useFlexiMigration} from "@irati/wallet/hooks/flexiMigration"
import {StyledContainer, ExpiringSoon} from "../styled"
import {useFlexiPointsWalletExpired} from "../../../hooks/expiry"
import {ConversionInfo} from "../../ConversionUpdate"

interface Props {
  adminRewardWallet: Wallet
  onClick: () => void
}

const AdminRewardBalance: FC<Props> = ({adminRewardWallet, onClick}) => {
  const {colors} = useTheme()
  const {load: expiryLoad, expiry} = useFlexiPointsWalletExpired(adminRewardWallet.uuid)

  const {load: migrationLoad, status} = useFlexiMigration()

  return (
    <Card small>
      <StyledContainer alignItems="center" justifyContent="space-around" gap={2}>
        <Flex direction="column" gap={1} width="100%">
          <Loading inline load={[migrationLoad, expiryLoad]}>
            {status && <ConversionInfo isAdmin amount={adminRewardWallet.amount ?? 0} migrationStatus={status} />}
            {expiry.hasExpiry && (
              <ExpiringSoon onClick={onClick}>
                <div>
                  <Flex>
                    <Label color={colors.utility.contrast} marginRight={1}>
                      <FormattedMessage id="wallet.availableBalance.info.expire" />
                    </Label>
                    <Badge padding={[0, 1]} weight="light" bgColor={colors.greys.graphGrey} color={colors.utility.contrast}>
                      {expiry.date}
                    </Badge>
                  </Flex>
                  <H3>
                    <FormattedCurrency currency={PaymentCurrencyType.Rp} value={expiry.amount} />
                  </H3>
                </div>
                <Icon color={colors.utility.ink} icon={IconTypes.circleInformation} large marginLeft={0.5} />
              </ExpiringSoon>
            )}
          </Loading>
        </Flex>
        <Flex direction="column" flex="1 0 max-content">
          <ButtonRouterLink
            id="rewardWalletTopUpButton"
            to="/flexi-checkout/top-up"
            onClick={() => tracking.buyPoints({source: "rp_non_budget_header"})}>
            <FormattedMessage id="wallet.reward.admin.btn.topup" />
          </ButtonRouterLink>
          <ButtonRouterLink
            id="rewardWalletDistributeRewardsButton"
            primary
            marginTop
            to="/flexi-assign/recipients"
            onClick={() => tracking.giveRewardPoints("rp_non_budget_header")}>
            <FormattedMessage id="wallet.reward.admin.btn.distribute" />
          </ButtonRouterLink>
          <ButtonRouterLink
            id="rewardWalletBrowseCatalogueButton"
            marginTop
            to="flexi-reward/categories"
            onClick={() => tracking.browseRewardCatalogue()}>
            <FormattedMessage id="wallet.reward.admin.btn.browse" />
          </ButtonRouterLink>
        </Flex>
      </StyledContainer>
    </Card>
  )
}

export default AdminRewardBalance
